/* VaultsComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");

const Renderer = require("./VaultsRenderer");
const GSO_ID = "565a65db-54e7-4461-a954-f0f38f310e19";

/**
 * @class VaultsComponent
 * @extends EventEmitter
 *
 * @fires VaultsComponent#Shutdown
 * @fires VaultsComponent#DataUpdated
 * @fires VaultsComponent#DirtyState
 * @fires VaultsComponent#OrganizationDataChange
 */

class VaultsComponent extends EventEmitter {
    constructor(userSession, uiutils, logger, userFeedback, SteveIo, Vaults, jnibAdapter) {
        super();
        this._userSession = userSession;
        this._logger = logger;
        this._modalUserFeedback = userFeedback;
        this._io = SteveIo;
        this._uiutils = uiutils;
        this._errors = undefined;
        this._nibApiService = jnibAdapter.getNibApiService();
        this._systemConfigEntity = undefined;
        this._bannerData = undefined;
        this._VaultsEntity = Vaults;
        // this._systemConfigFeed = undefined;

        this._organizationData = {};
        this._orgId = _.get(userSession, "activeOrganization.id");
        this._orgName = _.get(userSession, "activeOrganization.name");
        this._userAuthActions = _.get(userSession, "authorizedActions", []);
        this._isReadOnly = !this._userAuthActions.find("manageCredentials"); // update to ! from !!
        this.defaultProperties = {};
        this._updatedBannerData = {};
        this._isDirty = false;
        this._renderer = new Renderer(this, this._uiutils);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    organizationChanged(orgObj) {
        this._orgId = _.get(orgObj, "id");
        this._orgName = _.get(orgObj, "name");
        this._triggerDataUpdated();
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/
    getVaults(orgId, query) {
        return this._VaultsEntity.loadCredentialsSourceConfigs(orgId, query);
    }

    whenModelIsReady() {
        if (!_.isUndefined(this.activeOrgId)) {
            return Promise.all([this.getVaults(this.activeOrgId)]).then((values) => {
                this._vaults = values[0];
                this._slickData = this._generateSlickData(values[0]);
                return Promise.resolve();
            });
        } else {
            return Promise.reject();
        }
    }

    shutdown() {
        /**
         * @event VaultsComponent#Shutdown
         */
        this.emit("Shutdown");
        this._unregisterListeners();
        this.removeAllListeners();
        return Promise.all([this._renderer.shutdown()]).then(
            () => {
                this._systemConfigEntity = undefined;
                this._SystemConfig = undefined;
                this._isDirty = undefined;
                this._uiutils = undefined;
                this._errors = undefined;
                // this._systemConfigFeed = undefined;
            },
            (err) => {
                console.log("Error shutting down CustomFieldsComponent: ", err);
            }
        );
    }

    get logger() {
        return this._logger;
    }

    get userFeedback() {
        return this._modalUserFeedback;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getOrganizationData() {
        return this._organizationData;
    }

    get activeOrgIsRoot() {
        return this._organizationData.providerId === null;
    }

    // organizationChanged({ id, name }) {
    //     if (this._orgId === id) {
    //         return;
    //     }
    //     this._orgId = id;
    //     this._triggerOrganization({ id, name });
    // }

    _triggerDataUpdated() {
        /**
         * @event VaultsComponent#DataUpdated
         */
        this.emit("DataUpdated");
    }

    _triggerDirtyStateChanged() {
        /**
         * @event VaultsComponent#DirtyState
         */
        const resultState =
            this._osImageServerPrefs.isDirty() || this._osUpgradePlanPrefs.isDirty();
        this.emit("DirtyState", resultState);
    }

    _triggerOrganization(org) {
        /**
         * @event VaultsComponent#Organization
         * @param {{id: string, name: string}} - new organization parameters.
         */
        this.emit("Organization", org);
    }

    _triggerOrganizationDataChanged(orgData) {
        /**
         * @event VaultsComponent#OrganizationDataChange
         * @param {Object} - new organization data.
         */
        this.emit("OrganizationDataChange", orgData);
    }

    getActiveOrgId() {
        return this._orgId;
    }

    get myUserInfo() {
        return this._myUserInfo;
    }

    getSystemConfig() {
        return this._SystemConfig.loadSystemConfig(GSO_ID);
        // return sysDetailsFeed.whenInitialized();
    }

    _saveCredentials() {
        return Promise.resolve().then(() => {
            // if(!showVeil) {
        });
    }

    _displayError(err) {
        const errorMsg = err.reduce((msg, err) => {
            return `${msg}\n${err.key}: ${err.err}`;
        }, "");
        this._uiutils.writeErrorMessage(`Error: ${errorMsg}`);
    }

    _convertCustomFieldsDataToSlickTableFormat(data) {
        return (key) =>
            Object.assign({}, data[key], {
                id: key,
                inherited: this.inheritedKeys.includes(key),
            });
    }

    _generateSlickData(entities) {
        // console.log(entities);
        const slickData = entities.map((ety) => {
            return {
                id: ety._id,
                name: ety.name,
                vendor: ety.vendor,
                enabled: ety.tool === "gluware" ? true : !!ety.enabled,
                readOnly: ety.tool === "gluware" ? true : false,
            };
        });
        // console.log(slickData);
        return slickData;
    }

    deleteField(id) {
        // const workTarget = {
        //     method: "single",
        //     id: credentialId,
        // };

        return Promise.resolve()
            .then(() => {
                return this._VaultsEntity.delete(this._orgId, "CredentialsSourceConfig", id);
            })
            .then(() => {
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(err);
                const error = {
                    errorMsg: "Failed to delete",
                    errorCode: "DeleteError",
                };
                return Promise.reject(error);
            });
    }

    _formatErrorMessage(workResult) {
        const errorMessages = Object.values(workResult.targetErrors);
        const devPrefixPattern = new RegExp(
            "^The vaults named .* cannot be deleted because it .* in the ",
            "i"
        );

        const prefixPattern = new RegExp(
            "^The vaults named .* cannot be deleted because it .* in ",
            "i"
        );

        return errorMessages.reduce((accumulator, errorMessage) => {
            return [
                ...accumulator,
                errorMessage
                    .replace(devPrefixPattern, "The vault is used by ")
                    .replace(prefixPattern, "The vault is used in ") + ".",
            ];
        }, []);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = VaultsComponent;

VaultsComponent.$groupName = "Organization";

VaultsComponent.$displayName = "Vaults";
VaultsComponent.$license = ["GLUWARE"];

VaultsComponent.$inject = [
    "$teve.userSession",
    "$teve.ui.UiUtils",
    "$teve.logger",
    "$teve.ui.userFeedback",
    "$teve.io",
    "$teve.entities.CredentialsSourceConfig",
    "$teve.jnibAdapter",
];
